import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, MessageSquare, Search, Plus, Menu, X } from 'lucide-react';

import { useChatInteract } from '@chainlit/react-client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { useUISettings } from '@/hooks/useUISettings';
import './sidebar-custom.css';

import NewChatButton from '../header/NewChat';
import SearchChats from './Search';
import { ThreadHistory } from './ThreadHistory';
import ClearConversations from './ClearConversations';

export default function LeftSidebar() {
  const navigate = useNavigate();
  const { clear } = useChatInteract();
  const { settings } = useUISettings();
  const [isExpanded, setIsExpanded] = useState(() => {
    // Get initial state from localStorage, default to true
    const stored = localStorage.getItem('sidebar-expanded');
    return stored !== null ? JSON.parse(stored) : true;
  });
  
  // Get sidebar background color from settings
  const sidebarBg = settings?.colors?.sidebarBackground || '#ECF2FF';
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebar-expanded', JSON.stringify(isExpanded));
  }, [isExpanded]);

  // Close mobile sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.getElementById('mobile-sidebar');
      const toggleButton = document.getElementById('mobile-sidebar-toggle');
      
      if (isMobileOpen && 
          sidebar && 
          !sidebar.contains(event.target as Node) &&
          toggleButton &&
          !toggleButton.contains(event.target as Node)) {
        setIsMobileOpen(false);
      }
    };

    if (isMobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent body scroll when mobile sidebar is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleMobileSidebar = () => {
    setIsMobileOpen(!isMobileOpen);
  };  const handleNewChatClick = () => {
    if (isExpanded) {
      // The NewChatButton component handles its own action when expanded
      // We find the button and click it programmatically
      const newChatButton = document.getElementById('new-chat-button');
      if (newChatButton) {
        newChatButton.click();
      }
    } else {
      // For collapsed state, directly execute new chat action
      clear();
      navigate('/');
    }
    // Close mobile sidebar after action
    setIsMobileOpen(false);
  };

  return (
    <>
      {/* Mobile Toggle Button - Only visible on mobile */}
      <Button
        id="mobile-sidebar-toggle"
        onClick={toggleMobileSidebar}
        size="sm"
        variant="ghost"
        className={cn(
          'fixed top-4 left-4 z-50 md:hidden',
          'bg-background/80 backdrop-blur-sm border border-border',
          'hover:bg-accent',
          isMobileOpen && 'hidden' // Hide when sidebar is open
        )}
      >
        <Menu className="!size-5" />
      </Button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden" />
      )}

      {/* Desktop Sidebar */}
      <Sidebar 
        style={{ backgroundColor: sidebarBg }}
        className={cn(
          'custom-sidebar-bg transition-all duration-300 ease-in-out',
          // Desktop behavior
          'hidden md:block',
          isExpanded ? 'w-64' : 'w-8'
        )}
        collapsible="none"
      >
        {/* Header with toggle button and controls */}
        <SidebarHeader className={isExpanded ? 'p-3' : 'py-3 px-0 flex justify-center'}>
          <div className={`flex items-center ${isExpanded ? 'justify-between w-full' : 'justify-center'}`}>
            <Button
              onClick={toggleSidebar}
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-foreground shrink-0 w-8 h-8 p-1"
            >
              {isExpanded ? <PanelLeftClose className="!size-6" /> : <PanelLeftOpen className="!size-6" />}
            </Button>
            
            {isExpanded && (
              <div className="flex items-center space-x-2 ml-2">
                <SearchChats />
                <NewChatButton navigate={navigate} />
              </div>
            )}
          </div>
          
          {/* Section label when expanded */}
          {isExpanded && (
            <div className="mt-4">
              <h2 className="text-sm font-semibold text-gray-700 mb-2">Recent Chats</h2>
            </div>
          )}
        </SidebarHeader>

        {/* Content area */}
        <SidebarContent className="flex-1 overflow-hidden">
          {isExpanded ? (
            <ThreadHistory />
          ) : (
            <div className="flex flex-col items-center py-2">
              {/* Only toggle button visible in collapsed state due to narrow width */}
            </div>
          )}
        </SidebarContent>        {/* Footer with Clear Conversations button */}
        {isExpanded && (
          <SidebarFooter className="p-3 border-t border-sidebar-border">
            <ClearConversations />
          </SidebarFooter>
        )}      </Sidebar>

      {/* Mobile Sidebar Drawer */}
      <div
        id="mobile-sidebar"
        style={{ backgroundColor: sidebarBg }}
        className={cn(
          'fixed top-0 left-0 h-full w-80 max-w-[85vw] z-50 md:hidden',
          'transform transition-transform duration-300 ease-in-out',
          'shadow-xl border-r border-border',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Mobile Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-700">Chat History</h2>
            <Button
              onClick={toggleMobileSidebar}
              size="sm"
              variant="ghost"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="!size-5" />
            </Button>
          </div>
          
          <div className="flex items-center space-x-2 mt-3">
            <SearchChats />
            <NewChatButton navigate={navigate} />
          </div>
        </div>

        {/* Mobile Content */}
        <div className="flex-1 overflow-hidden p-4">
          <ThreadHistory />
        </div>

        {/* Mobile Footer */}
        <div className="p-4 border-t border-border">
          <ClearConversations />
        </div>
      </div>
    </>
  );
}
